import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Maximize, BedDouble, Zap, Check, ArrowLeft, Send } from "lucide-react";
import Seo from "@/components/Seo";
import { Field, inputCls } from "@/components/form";
import { api, formatApiError } from "@/lib/api";
import { fmtPrice } from "@/pages/Immobilien";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", gdpr_consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  useEffect(() => { api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => {}); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, business_area: "immobilien", source: "immobilie", request_type: "objektanfrage",
        property_id: id, topic: p?.title, message: `Anfrage zu: ${p?.title}\n${form.message}` });
      toast.success("Ihre Objektanfrage wurde gesendet. Wir melden uns in Kürze!");
      setForm({ name: "", email: "", phone: "", message: "", gdpr_consent: false });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!p) return <div className="min-h-screen bg-navy-900 flex items-center justify-center text-slate-400 pt-20">Lädt…</div>;

  return (
    <div data-testid="property-detail" className="bg-navy-900 min-h-screen">
      <Seo title={p.title} description={`${p.title} in ${p.city} – ${p.size_sqm} m², ${fmtPrice(p.price, p.deal_type)}. ${p.description?.slice(0, 120)}`} path={`/immobilien/${id}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": p.deal_type === "grundstueck" ? "Product" : "Residence",
        "name": p.title, "description": p.description,
        "address": { "@type": "PostalAddress", "addressLocality": p.city, "addressCountry": "DE" },
        "offers": { "@type": "Offer", "price": p.price, "priceCurrency": "EUR" } }) }} />

      <section className="pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Link to="/immobilien" className="text-slate-400 hover:text-gold-500 flex items-center gap-2 text-sm mb-6"><ArrowLeft size={16} /> Zurück zur Übersicht</Link>
          <div className="rounded-md overflow-hidden aspect-[16/9] max-h-[460px]"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /></div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            <div className="lg:col-span-2">
              <span className="text-xs uppercase tracking-wide bg-gold-500 text-navy-900 font-bold px-3 py-1 rounded-sm">{p.deal_type}</span>
              <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{p.title}</h1>
              <p className="mt-2 text-slate-400 flex items-center gap-2"><MapPin size={16} className="text-gold-500" /> {p.location}, {p.city}</p>
              <p className="mt-4 font-serif text-4xl text-gold-500">{fmtPrice(p.price, p.deal_type)}</p>

              <div className="mt-8 grid grid-cols-3 gap-4">
                {p.size_sqm > 0 && <div className="bg-navy-800 border border-white/10 rounded-md p-4 text-center"><Maximize className="text-gold-500 mx-auto" size={20} /><p className="text-white font-bold mt-2">{p.size_sqm} m²</p><p className="text-xs text-slate-500">Wohnfläche</p></div>}
                {p.rooms > 0 && <div className="bg-navy-800 border border-white/10 rounded-md p-4 text-center"><BedDouble className="text-gold-500 mx-auto" size={20} /><p className="text-white font-bold mt-2">{p.rooms}</p><p className="text-xs text-slate-500">Zimmer</p></div>}
                {p.energy_class && <div className="bg-navy-800 border border-white/10 rounded-md p-4 text-center"><Zap className="text-gold-500 mx-auto" size={20} /><p className="text-white font-bold mt-2">{p.energy_class}</p><p className="text-xs text-slate-500">Energieklasse</p></div>}
              </div>

              <h2 className="mt-10 text-xl font-bold text-white font-display">Beschreibung</h2>
              <p className="mt-3 text-slate-300 leading-relaxed whitespace-pre-line">{p.description}</p>

              {p.features?.length > 0 && <>
                <h2 className="mt-8 text-xl font-bold text-white font-display">Ausstattung</h2>
                <div className="mt-4 flex flex-wrap gap-3">
                  {p.features.map((f) => <span key={f} className="flex items-center gap-2 bg-navy-800 border border-white/10 rounded-sm px-4 py-2 text-sm text-slate-300"><Check size={14} className="text-gold-500" /> {f}</span>)}
                </div>
              </>}
            </div>

            <form data-testid="property-inquiry-form" onSubmit={submit} className="bg-navy-800 border border-white/10 rounded-md p-6 h-fit lg:sticky lg:top-24 space-y-4">
              <h3 className="font-display font-bold text-white text-lg">Objekt anfragen</h3>
              <Field label="Name *"><input data-testid="inquiry-name" required className={inputCls} value={form.name} onChange={set("name")} /></Field>
              <Field label="E-Mail *"><input data-testid="inquiry-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
              <Field label="Telefon"><input data-testid="inquiry-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
              <Field label="Nachricht"><textarea data-testid="inquiry-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Ich interessiere mich für dieses Objekt…" /></Field>
              <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
                <input data-testid="inquiry-gdpr" type="checkbox" checked={form.gdpr_consent} onChange={set("gdpr_consent")} className="mt-0.5 accent-gold-500 w-4 h-4" />
                <span>Ich stimme der Datenschutzerklärung zu.</span>
              </label>
              <button data-testid="inquiry-submit" disabled={loading} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-3 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors disabled:opacity-60">{loading ? "Sendet…" : <>Anfrage senden <Send size={15} /></>}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
